import * as React from 'react';
import { connect } from 'react-redux';
import { Observable } from 'rxjs';
import { createSelector } from 'reselect';
import type { OrganisationResponse } from 'rest/organisations';
import { fetchOrganisations } from 'rest/organisations';
import { Subject } from 'rxjs/Subject';
import type { Subscription } from 'rxjs/Subscription';
import type { State as AppState } from 'state';
import { getOrganisationsForCurrentUser } from 'state/selectors/request-create';
import type { Option, ValueType } from '@atlaskit/select--old';

import type { FieldFormProps } from '@atlassian/help-center-common-component/fields/types';
import { toSingleSelectValue } from '@atlassian/help-center-common-util/select';

import { OrganisationPickerDumb } from './organisation-picker-dumb';
import i18n from './organisation-picker-dumb/messages';

interface SelectorProps {
    organisations: OrganisationResponse[];
}

export interface Props extends FieldFormProps<string> {
    projectId: string | undefined;
    portalId: number;
    requestTypeId: number;
    organisationUserEmail: string;
    isNewReporter?: boolean;
    organisations: OrganisationResponse[];
    disabled: boolean;
    enableSubmitButton: () => void;
    disableSubmitButton: () => void;
    shareWithOrgByDefault: boolean;
}

export interface State {
    organisations: OrganisationResponse[] | undefined;
    selectedOrganisation?: Option<string>;
    isLoading: boolean;
}

export class OrganisationPicker extends React.PureComponent<Props & SelectorProps, State> {
    static defaultProps = {
        organisations: [],
    };

    state = {
        organisations: this.props.organisations,
        defaultUser: this.props.organisationUserEmail,
        selectedOrganisation: undefined,
        isLoading: false,
    };

    private reporterChanges = new Subject<{ projectId: string; organisationUserEmail: string }>();
    private reporterChangesSub: Subscription;

    componentDidMount() {
        const { organisations, enableSubmitButton } = this.props;

        if (organisations) {
            this.updateValue(organisations, true);
        }

        this.reporterChangesSub = this.reporterChanges
            .switchMap(({ projectId, organisationUserEmail }) => {
                const { isNewReporter } = this.props;
                if (isNewReporter) {
                    return Observable.of([]);
                }
                return (
                    fetchOrganisations(projectId, organisationUserEmail)
                        // If can error happens we'll just return an empty array so the stream stays subscribed.
                        .catch((_) => Observable.of([]))
                );
            })
            .subscribe(
                (updatedOrganisations) => {
                    enableSubmitButton();
                    this.setState({ organisations: updatedOrganisations, isLoading: false });
                    this.updateValue(updatedOrganisations, true);
                },
                () => {
                    enableSubmitButton();
                    this.setState({ organisations: undefined, isLoading: false });
                }
            );
    }

    componentWillUnmount() {
        this.reporterChangesSub.unsubscribe();
    }

    componentDidUpdate(prevProps: Props) {
        const { projectId, organisationUserEmail, disableSubmitButton } = this.props;
        const reporterUpdated = prevProps.organisationUserEmail !== organisationUserEmail;

        if (reporterUpdated && projectId) {
            disableSubmitButton();
            this.setState({ isLoading: true });
            this.reporterChanges.next({ projectId, organisationUserEmail });
        }
    }

    // notFromUserInteraction here is to mark the change event as being not the result of
    // a user interaction but rather a side effect of something else. This is primarily used for
    // analytics at the moment
    onChange = (value: ValueType<Option<string>>, notFromUserInteraction?: boolean) => {
        const organisation = toSingleSelectValue(value);
        if (organisation == null) {
            return;
        }
        this.setState({ selectedOrganisation: organisation });
        this.props.onChange?.(this.props.id, String(organisation.value), notFromUserInteraction);
    };

    // In the case where we have a single organisation, we want to select that as the default
    // otherwise it should be a Private request by default
    updateValue(organisations: OrganisationResponse[], notFromUserInteraction?: boolean) {
        if (organisations.length === 1 && this.props.shareWithOrgByDefault) {
            const [defaultOrg] = organisations;
            const selected = { label: defaultOrg.name, value: String(defaultOrg.id) };
            this.setState({ selectedOrganisation: selected });
            this.onChange(selected, notFromUserInteraction);
        } else {
            const privateRequest = { label: i18n.privateRequest.defaultMessage, value: '' };
            // @ts-ignore TS(2322) TypeScript upgrade 5.1.6, please fix this violation when you revisit this code.: Type '{ label: string | undefined; value: string; ... Remove this comment to see the full error message
            this.setState({ selectedOrganisation: privateRequest });
            // @ts-ignore TS(2345) TypeScript upgrade 5.1.6, please fix this violation when you revisit this code.: Argument of type '{ label: string | undefined; val... Remove this comment to see the full error message
            this.onChange(privateRequest, notFromUserInteraction);
        }
    }

    render() {
        const { organisations, isLoading, selectedOrganisation } = this.state;
        const { description, id, disabled } = this.props;

        if (!organisations || !organisations.length) {
            return null;
        }

        return (
            <OrganisationPickerDumb
                id={id}
                organisations={organisations}
                description={description}
                selected={selectedOrganisation}
                onSelectOrg={this.onChange}
                isLoading={isLoading}
                isDisabled={isLoading || disabled}
            />
        );
    }
}

const selector = createSelector(
    (state: AppState, props: Props) => {
        const organisations = getOrganisationsForCurrentUser(
            state,
            props.portalId,
            props.requestTypeId
        ) as OrganisationResponse[];
        return { organisations };
    },
    (organisationData) => organisationData
);

export default connect(selector)(OrganisationPicker);
